import { useEffect, useState } from "react";
import { Trash2, Library } from "lucide-react";
import { toast } from "sonner";
import { api, img, errMsg } from "../../../lib/api";
import { tx, useLang } from "../../../lib/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";

const AI_LABEL = { remove_bg: "Cutout", ai_edit: "AI-edited", generate: "AI-generated" };

export function LibraryGrid({ items, onPick, onArchive }) { useLang();
  if (!items.length) return <p className="py-6 text-center text-xs text-[var(--mv-dim)]" data-testid="library-empty">{tx("No saved images yet. Use SAVE in the Image Crop Studio.")}</p>;
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="library-grid">
      {items.map((m) => (
        <div key={m.media_id} className="group rounded-xl border border-[var(--mv-border)] p-2" data-testid={`library-item-${m.media_id}`}>
          <button onClick={() => onPick?.(m)} disabled={!onPick} className="mv-checker flex h-28 w-full items-center justify-center overflow-hidden rounded-lg" data-testid={`library-pick-${m.media_id}`}>
            <img src={img(m.url)} alt={m.name} className="max-h-full max-w-full object-contain" loading="lazy" />
          </button>
          <div className="mt-1.5 flex items-center gap-1">
            <span className="min-w-0 flex-1 truncate text-[11px]" title={m.name}>{m.name}</span>
            {onArchive && !m.in_use && <button onClick={() => onArchive(m)} aria-label={tx("Remove from library")} data-testid={`library-archive-${m.media_id}`}><Trash2 className="h-3.5 w-3.5 text-red-400" /></button>}
          </div>
          <p className="text-[10px] text-[var(--mv-dim)]">{m.width}×{m.height}{m.transparent ? ` · ${tx("transparent")}` : ""}{m.ai?.map((a) => ` · ${tx(AI_LABEL[a])}`).join("")}{m.in_use ? ` · ${tx("on homepage")}` : ""}</p>
        </div>
      ))}
    </div>
  );
}

export const useLibrary = () => {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/media-library").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const archive = async (m) => {
    if (!window.confirm(tx("Remove this image from the library?"))) return;
    try { await api.delete(`/admin/media-library/${m.media_id}`); load(); } catch (e) { toast.error(tx(errMsg(e))); }
  };
  return { items, load, archive };
};

export function LibraryPicker({ open, setOpen, onPick }) { useLang();
  const { items, load } = useLibrary();
  useEffect(() => { if (open) load(); }, [open]);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl border-[var(--mv-border)] bg-[var(--mv-card)]" data-testid="library-picker">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Library className="h-4 w-4 text-[var(--mv-teal)]" />{tx("Select from Image Library")}</DialogTitle></DialogHeader>
        <div className="max-h-[60vh] overflow-auto"><LibraryGrid items={items} onPick={(m) => { onPick(m); setOpen(false); }} /></div>
        <p className="text-[11px] text-[var(--mv-dim)]">{tx("Selecting only updates the preview. Click the object's Save button to change the live homepage.")}</p>
      </DialogContent>
    </Dialog>
  );
}
